import { AbstractControl } from '@angular/forms';
import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpClient,
  HttpHeaders
} from '@angular/common/http';
import { catchError, pluck, map } from 'rxjs/operators';
import {
  BehaviorSubject,
  Observable,
  forkJoin,
  Subject,
  Subscription,
  ReplaySubject,
  of
} from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Fixing,
  UpdateInfo,
  TreasuryDepositRateTenor,
  TreasuryDepositRateCurve,
  IRFixing
} from '@approot/models/markets';
import { AlertsService } from '../alerts/alerts.service';
import { AuthService } from '../auth/auth.service';
import { LegService } from '../leg/leg.service';
import {
  GetImpliedVolResponse,
  GetForwardPointsResponse,
  GetFixingsForCurrencyPairAndCodeResponse
} from '@approot/models';

export interface FxRate {
  currencyPair: string;
  ncfx_timestamp: string;
  timestamp: string;
  spot: {
    mid: number;
    spread: number;
  };
  isComposite: boolean;
  forwardPoints: ForwardPoints;
  forwardPointsMultiplier: number;
}

export type Tenors = Record<
  Period,
  Record<string, { bid: number; ask: number }>
>;

interface FxVolSurfaces {
  isComposite: boolean;
  timestamp: string;
  tenors: Tenors;
}

interface LiveMarketResponse {
  fxRates: Record<string, FxRate>;
  fxVolSurfaces: Record<string, FxVolSurfaces>;
}

interface LiveMarketData {
  status: string;
  response: LiveMarketResponse;
}

type ForwardPoints = Record<
  Period,
  {
    mid: number;
    spread: number;
  }
>;

type Period = 'ON' | '1W' | '2W' | '1M' | '2M' | '3M' | '6M' | '9M' | '1Y';

@Injectable({
  providedIn: 'root'
})
export class MarketsService {
  processingInfo = '';
  public showFixings = false;
  public showIRFixings = false;
  public showCreditLines = false;
  private _showDci = false;
  //loadedData = new Subject<TreasuryDepositRate[] | Fixing[]>(); // the data loaded from the database is emitted from here

  private _curveSaved = new ReplaySubject<boolean>();
  readonly curveSaved = this._curveSaved.asObservable();

  private _fixings = new BehaviorSubject<Fixing[]>([]);
  private _treasuryRateCurveTenors = new BehaviorSubject<
    TreasuryDepositRateTenor[]
  >([]);
  private _treasuryRateCurves = new BehaviorSubject<TreasuryDepositRateCurve[]>(
    []
  );

  readonly fixings = this._fixings.asObservable();
  readonly treasuryRateCurveTenors =
    this._treasuryRateCurveTenors.asObservable();
  readonly treasuryRateCurves = this._treasuryRateCurves.asObservable();

  deleteStatus = new Subject<any>(); // this subject is to recognize whether a deletion passed or failed
  timer: any; // store the timeout as we need to clear it in certain cases.
  currentAction: Subscription; // the current observable that is being carried out, e.g. a fetch or a save is stored here,
  // so that we can unsubscribe from it when the modal is closed.

  private marketStore: {
    treasuryRateCurveTenors: TreasuryDepositRateTenor[];
    fixings: Fixing[];
    treasuryRateCurves: TreasuryDepositRateCurve[];
  } = {
    treasuryRateCurveTenors: [],
    fixings: [],
    treasuryRateCurves: []
  };
  processingInfoNewCurve: string;

  constructor(
    private http: HttpClient,
    private alertsService: AlertsService,
    private authService: AuthService,
    private legService: LegService
  ) {}

  set showDci(val: boolean) {
    this._showDci = val;
  }

  get showDci() {
    return this._showDci;
  }

  getTreasuryRateCurve() {
    // this.processingInfo = 'Loading';
    // this.authService.currentSession().then(amazonCognito => {
    //   this.currentAction = this.http
    //     .get(
    //       'https://' +
    //         environment.api_domain +
    //         '.fxexotics.com/exotics/markets/treasury-rate-curve',
    //       this.getHeaders(amazonCognito.getIdToken().getJwtToken())
    //     )
    //     .pipe(catchError(this.handleError.bind(this)))
    //     .subscribe(
    //       (data: TreasuryDepositRateCurve[]) => {
    //         this.processingInfo = '';
    //         this.marketStore.treasuryRateCurves = data;
    //         this._treasuryRateCurves.next(
    //           Object.assign({}, this.marketStore).treasuryRateCurves
    //         );
    //       },
    //       error => {
    //         this._treasuryRateCurves.next([]);
    //         this.processingInterval('Failed to load curves');
    //         this.handleError(error);
    //       }
    //     );
    // });
  }

  // this is used top populate the observable that the treasury curve component consumes
  getTreasuryRateTenors(entity: string) {
    this.authService.currentSession().then(amazonCognito => {
      this.currentAction = this.http
        .get(
          'https://' +
            environment.api_domain +
            '.fxexotics.com/exotics/markets/tenor/' +
            entity,
          this.getHeaders(amazonCognito.getIdToken().getJwtToken())
        )
        .pipe(catchError(this.handleError()))
        .subscribe(
          (tenors: TreasuryDepositRateTenor[]) => {
            this.processingInfo = '';
            this.marketStore.treasuryRateCurveTenors =
              this.tenorReducer(tenors);
            this._treasuryRateCurveTenors.next(
              Object.assign({}, this.marketStore).treasuryRateCurveTenors
            );
          },
          error => {
            this._treasuryRateCurveTenors.next([]);
            this.processingInterval('Failed to load tenors');
            this.handleError('Failed to load tenors')(error).subscribe();
          }
        );
    });
  }

  clearTenors() {
    this.marketStore.treasuryRateCurveTenors = [];
    this._treasuryRateCurveTenors.next(
      Object.assign({}, this.marketStore).treasuryRateCurveTenors
    );
  }

  // this is used to directly return ALL the tenors for a given curve
  // must still filter to get latest with this.marketsService.tenorReducer
  // NOTE this function does not update the marketStore or the observables
  // TODO move the marketstore CRUD functions (functions that populate the observables)
  // into a different service and put that
  // service directly on the treasury-rate-component (i.e. declare in @Component)?
  getTreasuryRateTenorsForPricing(entity: string): Promise<Observable<any>> {
    return this.authService.currentSession().then(amazonCognito => {
      return this.http
        .get(
          'https://' +
            environment.api_domain +
            '.fxexotics.com/exotics/markets/tenor/' +
            entity,
          this.getHeaders(amazonCognito.getIdToken().getJwtToken())
        )
        .pipe(catchError(this.handleError()));
    });
  }

  // will return all available fixing sources, optionally pass in fixingSource code value also
  // optionally pass in date and fixingsource
  // date format is YYYY-MM-DD, a date range can also be applied YYYY-MM-DD-YYYY-MM-DD
  getMarketsFixingsForCross(
    currencyPair: string,
    date?: string,
    fixingSource?: string
  ) {
    let fs = fixingSource ? '/' + fixingSource : '';
    let d = date ? '/' + date : '';
    return this.authService
      .currentSession()
      .then(amazonCognito => {
        return this.http
          .get(
            'https://' +
              environment.api_domain +
              '.fxexotics.com/exotics/markets/fixing/' +
              currencyPair +
              d +
              fs,
            this.getHeaders(amazonCognito.getIdToken().getJwtToken())
          )
          .pipe(pluck('Items'));
        // pluck operator allows to specify the name of property
        // you wish to extract from the input's stream value.
      })
      .catch(error => {
        this.handleError('getMarketsFixingsForCross')(error).subscribe();
      });
  }

  public getLiveMarket(
    currencyPairs: string[]
  ): Promise<Observable<LiveMarketResponse>> {
    return this.authService.currentSession().then(cognito => {
      let payload = {
        currencyPairs
      };
      return this.http
        .post<LiveMarketData>(
          'https://' + environment.api_domain + '.fxexotics.com/fx/live-market',
          payload,
          this.getHeaders(cognito.getIdToken().getJwtToken())
        )
        .pipe(map(data => data.response));
    });
  }

  // we save a single curve at a time
  saveCurve(curve: TreasuryDepositRateCurve) {
    this.processingInfoNewCurve = 'Saving';
    this.authService.currentSession().then(amazonCognito => {
      this.http
        .put(
          'https://' +
            environment.api_domain +
            '.fxexotics.com/exotics/markets',
          curve,
          this.getHeaders(amazonCognito.getIdToken().getJwtToken())
        )
        .pipe(catchError(this.handleError()))
        .subscribe(
          x => {
            this.processingIntervalNewCurve('Saved');
            this.marketStore.treasuryRateCurves.push(curve);
            this._treasuryRateCurves.next(
              Object.assign({}, this.marketStore).treasuryRateCurves
            );
          },
          error => {
            this.processingIntervalNewCurve('Failed');
          }
        );
    });
  }

  saveTenor(
    tenors: TreasuryDepositRateTenor[],
    curvesControl: AbstractControl
  ) {
    // we pass in an array of the tenors, the whole form and the controls that are dirty.
    this.processingInfo = 'Saving';

    this.authService.currentSession().then(amazonCognito => {
      this.http
        .put(
          'https://' +
            environment.api_domain +
            '.fxexotics.com/exotics/markets',
          tenors,
          this.getHeaders(amazonCognito.getIdToken().getJwtToken())
        )
        .pipe(catchError(this.handleError()))
        .subscribe(
          x => {
            if (curvesControl) {
              // set the form to being pristine to remove all dirty changes
              curvesControl.markAsPristine();
              this.processingInterval('Saved');
              tenors.forEach(tenor => {
                // check if we are creating a new tenor, or editing an existing
                let index = this.marketStore.treasuryRateCurveTenors.findIndex(
                  x => x.tenor === tenor.tenor
                );
                if (index > -1) {
                  // if we are editing existing tenor, replace old with new
                  this.marketStore.treasuryRateCurveTenors[index] = tenor;
                } else {
                  this.marketStore.treasuryRateCurveTenors.push(tenor);
                }
              });
              this._treasuryRateCurveTenors.next(
                Object.assign({}, this.marketStore).treasuryRateCurveTenors
              );
            }
            this.processingInterval('Saved');
          },
          error => {
            console.log('error');
            this.processingInterval('Failed');
          }
        );
    });
  }

  saveFixing(fixings: Fixing[], fixingsControls: AbstractControl) {
    // we pass the fixing controls to set the form as pristine if the save succeeds
    // we pass dirty controls to set the control as saved when it is saved
    this.processingInfo = 'Processing';
    forkJoin(this.processFixingSaving(fixings))
      .pipe(
        map(x => {
          if (fixingsControls) {
            // set the form to being pristine to remove all dirty changes
            fixingsControls.markAsPristine();
          }
          this.processingInterval('Saved');
        }),
        catchError(error => {
          this.handleError();
          this.processingInterval('Failed');
          return of('');
        })
      )
      .subscribe();
  }

  public saveIRFixings(
    fixings: IRFixing[],
    fixingsControls: AbstractControl
  ): void {
    this.processingInfo = 'Processing';
    this.legService
      .marketDataService({
        task: 'setIrFixings',
        fixings
      })
      .pipe(
        map(x => {
          if (fixingsControls) {
            // set the form to being pristine to remove all dirty changes
            fixingsControls.markAsPristine();
          }
          this.processingInterval('Saved');
        }),
        catchError(error => {
          this.handleError();
          this.processingInterval('Failed');
          return of('');
        })
      )
      .subscribe();
  }

  processFixingSaving(
    fixings: Fixing[]
  ): Observable<
    | GetImpliedVolResponse
    | GetForwardPointsResponse
    | GetFixingsForCurrencyPairAndCodeResponse
    | unknown
  >[] {
    let batchOfFixings = [];
    fixings.map(fixing => {
      const { currencyPair, date, fixingSource, spot } = fixing;
      batchOfFixings.push(
        this.legService.marketDataService({
          task: 'setFxFixings',
          items: [
            {
              currencyPair,
              date,
              code: fixingSource,
              spot: parseFloat(spot)
            }
          ]
        })
      );
    });

    return batchOfFixings;
  }

  deleteRecords(dataToDelete: UpdateInfo[], control: AbstractControl) {
    this.processingInfo = 'Processing';

    this.authService
      .currentSession()
      .then(data => {
        forkJoin(this.prepareDeleteBatch(dataToDelete, data))
          .pipe(catchError(this.handleError()))
          .subscribe(
            x => {
              // remove all timestamps
              this.deleteStatus.next(true);
              if (control) {
                // set the form to being pristine to remove all dirty changes
                control.markAsPristine();
              }
              this.processingInterval('Saved');
            },
            error => {
              this.deleteStatus.next(false);

              console.log('error');
              this.processingInterval('Failed');
            }
          );
      })
      .catch(error => {
        this.deleteStatus.next(false);
      });
  }

  // these fork joins batch needs to be replaced with dynamoDB Batch

  prepareDeleteBatch(recordsToDelete: UpdateInfo[], amazonCognito) {
    // the current users userId
    const currentUserUserId = amazonCognito.getIdToken().payload.sub;
    // create a batch of observables that will be subscribed to
    let observableBatch = [];
    recordsToDelete.forEach(element => {
      const userId = element.userId ? element.userId : currentUserUserId;
      observableBatch.push(
        this.http.delete(
          'https://' +
            environment.api_domain +
            '.fxexotics.com/exotics/markets/object/' +
            amazonCognito.getIdToken().payload['cognito:groups'][0] +
            '/' +
            userId +
            '/' +
            element.timestamp,
          this.getHeaders(amazonCognito.getIdToken().getJwtToken())
        )
      );
    });

    return observableBatch;
  }

  processingInterval(text: string) {
    this.processingInfo = text;
    this.timer = setTimeout(() => {
      this.processingInfo = '';
    }, 6000);
  }

  processingIntervalNewCurve(text: string) {
    this.processingInfoNewCurve = text;
    this.timer = setTimeout(() => {
      this.processingInfoNewCurve = '';
      this._curveSaved.next(true);
    }, 2000);
  }

  private handleError<T>(operation = 'operation') {
    return (error: HttpErrorResponse): Observable<T> => {
      this.alertsService.addAlert({ message: error.error });
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      const message =
        error.error instanceof ErrorEvent
          ? error.error.message
          : `server returned code ${error.status} with body "${error.error}"`;

      // TODO: better job of transforming error for user consumption
      throw new Error(`${operation} failed: ${message}`);
    };
  }

  // in the markets table, there might be multiple fixings
  // for a day/currencypair, so get the most recent one
  fixingReducer(fixings: Fixing[]) {
    return Object.values(
      fixings.reduce((a, b) => {
        if (a[b.date]) {
          if (a[b.date].timestamp < b.timestamp) a[b.date] = b;
        } else a[b.date] = b;

        return a;
      }, {})
    ) as Fixing[];
  }

  // in the markets table, if we have multiple tenors for a
  // curve, then choose the most recent version
  tenorReducer(
    fixings: TreasuryDepositRateTenor[]
  ): TreasuryDepositRateTenor[] {
    return Object.values<TreasuryDepositRateTenor>(
      fixings.reduce((a, b) => {
        if (a[b.tenor]) {
          if (a[b.tenor].timestamp < b.timestamp) {
            a[b.tenor] = b;
          }
        } else {
          a[b.tenor] = b;
        }

        return a;
      }, {})
    );
  }

  private getHeaders(token: string) {
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', token);
    return { headers };
  }
}
